"use client";

import type { FC, ReactNode } from "react";
import {
  AdjustmentsHorizontalIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  LanguageIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import type { TaxonomyOption } from "cmw-client";
import { Button } from "@/components/button";
import { MultiSelect, NestedMultiCombobox } from "@/components/input";
import { AllFiltersTrigger } from "./all-filters-trigger";
import type { DirectoryFilters, FilterKey } from "./filters";
import { MobileFilterBar } from "./mobile-filter-bar";
import { GENDER_OPTIONS, toFilterGroups, toFilterOptions } from "./options";

type DirectoryFilterBarProps = {
  filters: DirectoryFilters;
  onChange: (filters: DirectoryFilters) => void;
  taxonomy?: Record<string, TaxonomyOption[]>;
  /** Mobile only: below `md` the list/map switch lives in the bar itself (Figma 1068:31085). */
  viewMode?: "list" | "map";
  onViewModeChange?: (viewMode: "list" | "map") => void;
};

const SUBGRID = "lg:row-span-2 lg:grid lg:grid-rows-subgrid lg:gap-8";

const label = (icon: ReactNode, text: string, hint?: string) => (
  <span className="flex items-center gap-8">
    <span className="text-brand-blue">{icon}</span>
    {text}
    {hint && <InformationCircleIcon className="size-18 text-neutral-grey" aria-label={hint} />}
  </span>
);

export const DirectoryFilterBar: FC<DirectoryFilterBarProps> = ({
  filters,
  onChange,
  taxonomy,
  viewMode,
  onViewModeChange,
}) => {
  const set = (key: FilterKey) => (values: string[]) => onChange({ ...filters, [key]: values });

  /* Two separate layouts rather than one that reflows: below `md` the bar is the Figma mobile
     design (1068:31085) — a list/map switch, an "All filters" sheet and the active filters as
     removable chips — and the three inline selects don't appear at all. Swapped with a media
     query, not `useIsMobile`, so a phone never paints the desktop grid before hydration. */
  return (
    <div className="flex flex-col gap-16">
      <MobileFilterBar
        className="md:hidden"
        filters={filters}
        onChange={onChange}
        taxonomy={taxonomy}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
      />

      <div className="grid gap-16 max-md:hidden lg:grid-cols-[repeat(3,minmax(0,1fr))_auto] lg:grid-rows-[auto_auto] lg:items-start">
        <MultiSelect
          containerClassName={SUBGRID}
          label={label(<UsersIcon className="size-18" />, "Gender preference")}
          placeholder="Please select"
          maxVisibleTags={2}
          options={GENDER_OPTIONS}
          value={filters.gender ?? []}
          onValueChange={set("gender")}
        />
        <NestedMultiCombobox
          containerClassName={SUBGRID}
          label={label(<DocumentTextIcon className="size-18" />, "Areas of support", "Matches a practitioner's focus areas")}
          placeholder="Showing all"
          maxVisibleTags={2}
          groups={toFilterGroups(taxonomy?.focusAreas)}
          value={filters.focusAreas ?? []}
          onValueChange={set("focusAreas")}
        />
        <MultiSelect
          containerClassName={SUBGRID}
          label={label(<LanguageIcon className="size-18" />, "Preferred language")}
          placeholder="Please select"
          maxVisibleTags={2}
          options={toFilterOptions(taxonomy?.languages)}
          value={filters.languages ?? []}
          onValueChange={set("languages")}
        />
        <AllFiltersTrigger filters={filters} onChange={onChange} taxonomy={taxonomy}>
          <Button
            variant="secondary-white"
            size="l"
            icon={<AdjustmentsHorizontalIcon className="size-20" />}
            className="lg:col-start-4 lg:row-start-2 lg:shrink-0"
          >
            All Filters
          </Button>
        </AllFiltersTrigger>
      </div>
    </div>
  );
};
